import API from '../../../../lib/api'
import { ORDER_DETAILS_API_URL } from '../../DeliveryOrdersPicking/constants/pagination'
import { isDspSource } from '../constants/orderSource'
import { GROUP_ROLE } from './poaPacklistGroups'

/**
 * Fetches the packlists of the orders linked to a DSP order by POA ("post-order
 * amendment"), shaped as the `packlistGroups` that normalisePacklistGroups reads.
 *
 * picking-service returns them from `/v3/packlists?withAmendment=true` (LIT-540), but only
 * on the reference-number path and only when that reference is the MAIN order: the
 * response is the main order's `packlists` plus every amendment order's packlists flat in
 * `amendments`. So from an amended order the main order is resolved first, through the
 * orderDetails row whose childOrderId is the amended order's poaChildOrderId.
 *
 * Display-only and additive: the opened order's own packlists still come from the
 * existing call, so every write path is untouched. Returns null when the order has no POA
 * relationship, which leaves the page exactly as it renders without one.
 */

const hasAmendments = (order) =>
  Array.isArray(order?.amendments) && order.amendments.length > 0

export const isPoaLinkedOrder = (order) =>
  isDspSource(order?.source) &&
  (hasAmendments(order) || Boolean(order?.poaChildOrderId))

const resolveMainOrder = async (order) => {
  if (!order?.poaChildOrderId) {
    return order
  }
  const api = new API({ url: ORDER_DETAILS_API_URL })
  const response = await api.get({
    childOrderId: order.poaChildOrderId,
    pageSize: 1,
  })
  return response?.orderDetails?.[0] || null
}

const toArray = (value) => (Array.isArray(value) ? value : [])

/**
 * Groups the withAmendment response by order: the main order first, then one group per
 * amendment order in the order the main order's row lists them. Amendment packlists are
 * matched to their order by packlist.orderId.
 */
export const buildPoaPacklistGroups = (mainOrder, response) => {
  const amendmentPacklists = toArray(response?.amendments)

  const amendmentGroups = toArray(mainOrder?.amendments).map((amendment) => ({
    deliveryOrderNo: amendment.referenceNumber || '',
    role: GROUP_ROLE.AMENDED,
    status: amendment.status || '',
    addedAt: amendment.placedOn || '',
    packlists: amendmentPacklists.filter(
      (packlist) => String(packlist?.orderId) === String(amendment.orderId)
    ),
  }))

  return [
    {
      deliveryOrderNo: mainOrder?.referenceNumber || '',
      role: GROUP_ROLE.MAIN,
      status: mainOrder?.status || '',
      addedAt: '',
      packlists: toArray(response?.packlists),
    },
    ...amendmentGroups,
  ]
}

export const fetchPoaPacklistGroups = async (order) => {
  if (!isPoaLinkedOrder(order)) {
    return null
  }

  const mainOrder = await resolveMainOrder(order)
  if (!mainOrder?.referenceNumber || !hasAmendments(mainOrder)) {
    return null
  }

  const api = new API({
    url: `/picking-service/v3/packlists?deliveryOrderReferenceNumber=${encodeURIComponent(
      mainOrder.referenceNumber
    )}&withAmendment=true`,
  })
  const response = await api.get({})

  return buildPoaPacklistGroups(mainOrder, response)
}

export default fetchPoaPacklistGroups
